import { debounce, throttle } from 'lodash';
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  FileLoader,
  Float32BufferAttribute,
  Group,
  InterleavedBufferAttribute,
  Points,
  ShaderMaterial,
} from 'three';

import { frameAnnoStore, StoreType } from '@/features/anno/stores';
import { buildBVHTreeForGroup, PCDLoader } from '@/lib/three';
import type { LoadStatus, Rawdata } from '@/types';
import { FastKmeans, isIdentityMatrix4, normalize } from '@/utils/math';

import { RawLoadOptions, RawObject, RawObjectOptions, RawObjectTheme } from './RawObject';
import { SseEditor } from './SseEditor';

const themeColors: Record<string, number[]> = {
  algor: [
    // morandi
    0x0d6a73, 0x6374d6, 0x69a0c7, 0x7ba68d, 0xbf9c73, 0xc2bb91, 0x2b758c, 0x7e9680, 0x7f78d6, 0x9199c2, 0xb1b274,
  ],

  // [dark, light]
  binarization: [0x0d6a73, 0x69a0c7],

  // linear morandi
  linear: [0x0d6a73, 0x69a0c7, 0xb1b274, 0xbf9c73, 0x9199c2, 0x7f78d6, 0x6374d6],

  // [灰，绿，蓝]
  height: [0x505050, 0x74a88a, 0x1f4968],
};

const COLOR_GROUND_GREY = 0x808080;

const DISTANCE_SQUARE_MAX = 32768; // 2^15

const createDownloadEvent = (sizes: Array<{ size: number }>, onProgress?: (data: LoadStatus) => void) => {
  if (!onProgress) return {};

  const totalSize = sizes.reduce((prev, { size }) => prev + size, 0);

  const throttleOnProgress = throttle((data) => onProgress(data), 1000);

  let prevLoaded = 0;
  let index = 0;

  const onLoaded = () => {
    prevLoaded += sizes[index].size;
    index++;

    if (index === sizes.length) {
      throttleOnProgress.cancel();
      onProgress({
        info: 'loading',
      });
    } else {
      onProgress({
        info: 'downloading',
        progress: Math.round((prevLoaded / totalSize) * 100),
      });
    }
  };
  const onLoading = (progress: ProgressEvent) => {
    const currentLoad = prevLoaded + progress.loaded;

    const percent = Math.round((currentLoad / totalSize) * 100);

    throttleOnProgress({
      info: 'downloading',
      progress: Math.min(percent, 100),
    });
  };

  return {
    onLoading,
    onLoaded,
  };
};

export class RawPoints extends RawObject {
  /** 智能选项对应的具体策略，目前有聚类、线性两种策略可选；聚类计算开销较大，故默认为线性着色 */
  static ALGOR_STRATEGY: 'intensityLinearSort' | 'intensityCluster' = 'intensityLinearSort';

  public object: Group;
  public zRange: [number, number];
  public needBVH: boolean;
  public intensityRange?: [number, number];
  public scheduler: SseEditor | undefined;

  private clusterColors: Map<string, Float32Array>;
  private updateGroundZ: () => void;
  private updateIntensitySeg: () => void;
  private updateHeightSeg: () => void;

  constructor(data: Rawdata) {
    super(data.name, data.type);
    this.object = new Group();
    this.clusterColors = new Map();
    this.zRange = [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
    this.needBVH = true;
    this.scheduler = undefined;

    this.updateGroundZ = debounce(() => {
      this.object.children.forEach((points) => {
        this.clusterColors.delete(points.uuid);
        this.updatePointsColor(points as Points<BufferGeometry, ShaderMaterial>);
      });
    }, 1000);
    this.updateIntensitySeg = throttle(() => {
      this.object.children.forEach((points) => {
        this.updatePointsColor(points as Points<BufferGeometry, ShaderMaterial>);
      });
    }, 400);
    this.updateHeightSeg = throttle(() => {
      this.object.children.forEach((points) => {
        this.updatePointsColor(points as Points<BufferGeometry, ShaderMaterial>);
      });
    }, 300);
  }

  static getAvailableThemes(intensityRange?: [number, number]): Array<RawObjectTheme> {
    return Boolean(intensityRange) ? ['origin', 'algor', 'binarization', 'height', 'intensity'] : ['origin', 'height'];
  }

  /**
   * 根据 intensity 着色
   * @param intensity
   * @returns
   */
  private getColorsByIntensity(intensity: BufferAttribute | InterleavedBufferAttribute) {
    const intensityArray = intensity.array;
    const colors = new Float32Array(intensityArray.length * 3);
    const max = this.intensityRange?.[1] ?? 255;
    const mid = Math.ceil(max / 2);
    for (let i = 0; i < intensityArray.length; i++) {
      // 计算当前强度值对应的颜色值
      const value = intensityArray[i] * Math.floor(max / this.options.intensitySeg);
      const cr = value > mid ? (value - mid) * 2 : 0;
      const cg = value < mid ? 2 * value : max - (value - mid) * 2;
      const cb = value < mid ? max - 2 * value : 0;
      // 将颜色值归一化并存储到 colors 数组中
      colors[i * 3] = cr / max;
      colors[i * 3 + 1] = cg / max;
      colors[i * 3 + 2] = cb / max;
    }
    return colors;
  }

  async load(data: Rawdata, { coord, onProgress }: RawLoadOptions) {
    const urls = await RawObject.resolveRawUrl(data);

    const fileLoader = new FileLoader();
    fileLoader.setResponseType('arraybuffer');

    // 创建点云解析器，用于将 Arraybuffer 解析为 Points
    const pcdParser = new PCDLoader();
    const fileToThreeMat = coord.matrixs[1];

    let cameraPosition: [number, number, number] | undefined = undefined;

    if (!isIdentityMatrix4(fileToThreeMat)) {
      // 如果转换矩阵非单位矩阵，则点云加载过程中会自动将中心点调整至原点，加载完成后无需调整相机位置
      pcdParser.setCorrectMatrix(fileToThreeMat);
      cameraPosition = [0, 0, 0];
    }

    const { onLoading, onLoaded } = createDownloadEvent(urls, onProgress);

    for (let i = 0; i < urls.length; i++) {
      const buffer = await fileLoader.loadAsync(urls[i].url, onLoading);
      onLoaded?.();

      // 大点云解析时间较长，异步调用以确保优先更新全局状态
      await new Promise((resolve) => {
        setTimeout(() => {
          if (buffer instanceof ArrayBuffer) {
            pcdParser.parseBuffer(buffer).forEach((points) => {
              // 点云对象 name 须与源文件 name 保持一致，用于后续查找
              points.name = data.name;
              this.initialPoints(points);
              this.object.add(points);
            });
          }
          resolve(this.object);
        }, 0);
      });
    }

    if (!cameraPosition) {
      cameraPosition = this.getCameraPosition();
    }

    if (this.needBVH) {
      buildBVHTreeForGroup(this.object, () => {
        onProgress?.({
          info: 'finish',
        });
      });

      onProgress?.({
        info: 'computing',
      });
    } else {
      onProgress?.({
        info: 'finish',
      });
    }

    return { cameraPosition };
  }

  private initialPoints(points: Points<BufferGeometry, ShaderMaterial>) {
    points.material.uniforms.size.value = this.options.pointSize;
    points.material.uniforms.minZ.value = this.options.visibleZ[0];
    points.material.uniforms.maxZ.value = this.options.visibleZ[1];

    if (!points.geometry.attributes.color) {
      // 原始文件无颜色数据时，使用纯白色做默认色值
      const colors = new Float32BufferAttribute(
        new Float32Array(points.geometry.attributes.position.array.length).fill(0.8),
        3,
      );
      points.geometry.setAttribute('color', colors);
    }
    // 存储默认色值
    points.geometry.setAttribute('color0', points.geometry.attributes.color);

    this.updatePointsMinAndMax(points);
    this.updatePointsColor(points);
  }

  private getCameraPosition(): [number, number, number] {
    if (this.object.children.length === 0) return [0, 0, 0];

    let inSight = false;

    this.object.children.forEach((child) => {
      const positions = (child as Points).geometry.attributes.position.array;
      for (let i = 0; i < positions.length; i += 3) {
        const dis = positions[i] * positions[i] + positions[i + 1] * positions[i + 1];
        if (dis <= DISTANCE_SQUARE_MAX) {
          // 如果点云任意一点小于距离上限，则说明点云在原点附近，中断循环
          inSight = true;
          break;
        }
      }
    });

    // 点云不在当前视野内时，调整相机
    return inSight
      ? [0, 0, 0]
      : (Array.from((this.object.children[0] as Points).geometry.attributes.position.array.slice(0, 3)) as [
          number,
          number,
          number,
        ]);
  }

  public updateRenderOptions(options: RawObjectOptions, forceUpdate = false) {
    let toUpdate: RawObjectOptions = {};

    if (forceUpdate) {
      toUpdate = options;
    } else {
      // 过滤出需要更新的值
      Object.entries(options).forEach(([key, val]) => {
        if (val !== undefined && val !== this.options[key as keyof RawObjectOptions]) {
          toUpdate[key as keyof RawObjectOptions] = val as any;
        }
      });
    }

    this.options = Object.assign(this.options, toUpdate);

    let key: keyof RawObjectOptions;
    for (key in toUpdate) {
      switch (key) {
        case 'groundZ':
          if (this.options.colorTheme === 'algor') {
            this.updateGroundZ();
          }
          break;
        case 'colorTheme':
          this.object.children.forEach((points) => {
            this.updatePointsColor(points as Points<BufferGeometry, ShaderMaterial>);
          });
          break;
        case 'pointSize':
          this.object.children.forEach((points) => {
            (points as Points<BufferGeometry, ShaderMaterial>).material.uniforms.size.value = this.options.pointSize;
          });
          break;
        case 'visibleZ':
          this.object.children.forEach((points) => {
            (points as Points<BufferGeometry, ShaderMaterial>).material.uniforms.minZ.value = this.options.visibleZ[0];
            (points as Points<BufferGeometry, ShaderMaterial>).material.uniforms.maxZ.value = this.options.visibleZ[1];
          });
          break;
        case 'intensitySeg':
          this.updateIntensitySeg();
          break;
        case 'heightSeg':
          this.updateHeightSeg();
          break;
        case 'opacity':
          this.object.children.forEach((points) => {
            (points as Points<BufferGeometry, ShaderMaterial>).material.transparent = true;
            (points as Points<BufferGeometry, ShaderMaterial>).material.uniforms.alphaValue.value =
              this.options.opacity;
          });
          break;
        default:
          break;
      }
    }
  }

  private updatePointsColor(points: Points<BufferGeometry, ShaderMaterial>) {
    const { intensity, position, color0 } = points.geometry.attributes;
    const { colorTheme, groundZ } = this.options;

    let colors: Float32Array | undefined;

    if (colorTheme === 'algor' && intensity && this.intensityRange) {
      switch (RawPoints.ALGOR_STRATEGY) {
        case 'intensityCluster':
          colors = this.clusterColors.get(points.uuid);
          if (!colors) {
            colors = this.intensityCluster(intensity, position, themeColors[colorTheme], groundZ);
            this.clusterColors.set(points.uuid, colors);
          }
          break;
        case 'intensityLinearSort':
          colors = this.intensityLinear(intensity, this.intensityRange[1] - this.intensityRange[0], position, groundZ);
          break;
      }
    } else if (colorTheme === 'binarization' && intensity) {
      colors = this.intensityBinarization(intensity);
    } else if (colorTheme === 'height') {
      colors = this.heightLinearSegment(position);
    } else if (colorTheme === 'intensity' && intensity) {
      colors = this.getColorsByIntensity(intensity);
    }

    if (colors) {
      // 将计算得到的颜色信息设置到点云的颜色属性上
      points.geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    } else if (color0) {
      // 关联错误处理：https://sentry.np.konvery.work/organizations/sentry/issues/27/?query=is%3Aunresolved&referrer=issue-stream
      // 没有计算结果，使用默认色值
      points.geometry.setAttribute('color', color0);
    }

    points.geometry.attributes.color.needsUpdate = true;
  }

  // todo: 遍历放在 pcdLoader 中
  private updatePointsMinAndMax(points: Points<BufferGeometry, ShaderMaterial>) {
    const { position, intensity } = points.geometry.attributes;
    for (let i = 2; i < position.array.length; i += 3) {
      this.zRange[0] = Math.min(this.zRange[0], position.array[i]);
      this.zRange[1] = Math.max(this.zRange[1], position.array[i]);
    }
    if (intensity) {
      let min = this.intensityRange?.[0] ?? Number.MAX_SAFE_INTEGER;
      let max = this.intensityRange?.[1] ?? Number.MIN_SAFE_INTEGER;

      for (let i = 0; i < intensity.array.length; i++) {
        min = Math.min(min, intensity.array[i]);
        max = Math.max(max, intensity.array[i]);
      }

      this.intensityRange = [min, max];
    } else {
      this.intensityRange = undefined;
    }
  }

  private intensityLinear(
    intensity: BufferAttribute | InterleavedBufferAttribute,
    intensityRange: number,
    position: BufferAttribute | InterleavedBufferAttribute,
    ground: number,
  ) {
    const zValues = new Array(Math.ceil(position.array.length / 3)).fill(0).map((_, i) => position.array[i * 3 + 2]);

    // 设定 z 值小于 ground 的点为地面点
    const lowGroundIndices = zValues.reduce((indices: number[], zValue, i) => {
      if (zValue < ground) indices.push(i);
      return indices;
    }, []);

    // 设定 z 值大于等于 ground 的为非地面点
    const highGroundIndices = zValues.reduce((indices: number[], zValue, i) => {
      if (zValue >= ground) indices.push(i);
      return indices;
    }, []);

    // 对地面点的颜色值设定为灰色
    const groundColor = new Color(COLOR_GROUND_GREY);
    const groundColors = new Float32Array(lowGroundIndices.length * 3).fill(0);
    for (let i = 0; i < lowGroundIndices.length; i++) {
      groundColor.toArray(groundColors, i * 3);
    }

    if (highGroundIndices.length > 0) {
      const data = highGroundIndices.map((i) => intensity.array[i]);
      const colorList = themeColors.linear.map((clr) => new Color(clr));
      const colors = new Float32Array(data.length * 3);
      const bucketSize = Math.ceil(intensityRange / colorList.length);
      const minIntensity = this.intensityRange?.[0] ?? 0;
      for (let i = 0; i < data.length; i++) {
        const colorIndex = Math.floor((data[i] - minIntensity) / bucketSize);
        const colorCode = colorList[colorIndex];
        colorCode.toArray(colors, i * 3);
      }

      // 将地面点和非地面点的颜色信息合并
      const allColors = new Float32Array(position.array.length);
      lowGroundIndices.forEach((index: number, i: number) => {
        allColors[index * 3] = groundColors[i * 3];
        allColors[index * 3 + 1] = groundColors[i * 3 + 1];
        allColors[index * 3 + 2] = groundColors[i * 3 + 2];
      });
      highGroundIndices.forEach((index, i) => {
        allColors[index * 3] = colors[i * 3];
        allColors[index * 3 + 1] = colors[i * 3 + 1];
        allColors[index * 3 + 2] = colors[i * 3 + 2];
      });
      return allColors;
    } else {
      return groundColors;
    }
  }
  /**
   * 根据 intensity 着双色
   * @param intensity
   * @returns
   */
  private intensityBinarization(intensity: BufferAttribute | InterleavedBufferAttribute) {
    const intensityArray = intensity.array;
    const colors = new Float32Array(intensityArray.length * 3);
    const [dark, light] = themeColors.binarization.map((clr) => new Color(clr));
    for (let i = 0; i < intensityArray.length; i++) {
      if (intensityArray[i] > this.options.intensitySeg) {
        light.toArray(colors, i * 3);
      } else {
        dark.toArray(colors, i * 3);
      }
    }
    return colors;
  }

  /**
   * 根据 z 值着色
   * @param intensity
   * @returns
   */
  private heightLinearSegment(position: BufferAttribute | InterleavedBufferAttribute) {
    const colors = new Float32Array(position.array.length);
    const [ground, light, dark] = themeColors.height.map((clr) => new Color(clr));
    for (let i = 0; i < position.array.length; i = i + 3) {
      if (position.array[i + 2] < this.options.heightSeg[0]) {
        ground.toArray(colors, i);
      } else if (
        position.array[i + 2] >= this.options.heightSeg[0] &&
        position.array[i + 2] < this.options.heightSeg[1]
      ) {
        light.toArray(colors, i);
      } else {
        dark.toArray(colors, i);
      }
    }
    return colors;
  }

  /**
   * 对 (x,y,z,材质值归一化) kmeans 聚类
   * @param intensity 强度信息
   * @param position 位置信息
   * @param colorHub 色板
   * @param ground 地面高度
   * @returns
   */
  private intensityCluster(
    intensity: BufferAttribute | InterleavedBufferAttribute,
    position: BufferAttribute | InterleavedBufferAttribute,
    colorHub: Array<number>,
    ground: number,
  ) {
    const intensityArray = intensity.array;
    const zValues = new Array(Math.ceil(position.array.length / 3)).fill(0).map((_, i) => position.array[i * 3 + 2]);

    // 聚类数量
    const k = 130;

    // 材质值归一化区间长度
    const normalization = 20;

    // 设定 z 值小于 ground 的点为地面点，染成灰色
    const lowGroundIndices = zValues.reduce((indices: number[], zValue, i) => {
      if (zValue < ground) indices.push(i);
      return indices;
    }, []);

    // 设定 z 值大于等于 ground 的为非地面点，进行 k-means 聚类
    const highGroundIndices = zValues.reduce((indices: number[], zValue, i) => {
      if (zValue >= ground) indices.push(i);
      return indices;
    }, []);

    // 对地面点的颜色值设定为灰色
    const groundColor = new Color(COLOR_GROUND_GREY);
    const groundColors = new Float32Array(lowGroundIndices.length * 3).fill(0);
    for (let i = 0; i < lowGroundIndices.length; i++) {
      groundColor.toArray(groundColors, i * 3);
    }

    if (highGroundIndices.length > 0) {
      // 对点云的 intensity 进行归一化，将其缩放到 [0, normalization] 范围内
      const normalizedIntensity = normalize(intensityArray, normalization);

      // 提取非地面点的坐标和归一化后的 intensity 信息，作为聚类的输入数据
      const data = highGroundIndices.map((i) => [
        position.array[i * 3],
        position.array[i * 3 + 1],
        position.array[i * 3 + 2],
        normalizedIntensity[i] ? normalizedIntensity[i] : 1,
      ]);

      // 进行 k-means 聚类，将非地面点分成 k 类
      const kmeans = new FastKmeans(data, k);
      const clusters = kmeans.run();

      // 生成颜色对象
      const colorHubMapped = colorHub.map((colorHex) => new Color(colorHex));

      // 根据聚类结果对点云上色
      const colors = new Float32Array(data.length * 3);
      clusters.forEach((cluster, i) => {
        // 循环使用主题色
        const colorIndex = i % colorHub.length;
        const colorCode = colorHubMapped[colorIndex];
        cluster.forEach((index: number) => {
          colorCode.toArray(colors, index * 3);
        });
      });

      // 将地面点和非地面点的颜色信息合并
      const allColors = new Float32Array(position.array.length);
      lowGroundIndices.forEach((index: number, i: number) => {
        allColors[index * 3] = groundColors[i * 3];
        allColors[index * 3 + 1] = groundColors[i * 3 + 1];
        allColors[index * 3 + 2] = groundColors[i * 3 + 2];
      });
      highGroundIndices.forEach((index, i) => {
        allColors[index * 3] = colors[i * 3];
        allColors[index * 3 + 1] = colors[i * 3 + 1];
        allColors[index * 3 + 2] = colors[i * 3 + 2];
      });
      return allColors;
    } else {
      return groundColors;
    }
  }

  public initialSegment(store: StoreType) {
    this.scheduler = new SseEditor(this.object, store as typeof frameAnnoStore);
    this.scheduler?.initData();
  }

  public setLabelEnable = (enable = true) => {
    if (enable) {
      this.scheduler?.updatePointCloudColor();
    } else {
      this.scheduler?.reset();
    }
  };
}
